import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { AlertCircle, CheckCircle2, ChevronLeft, MapPin } from 'lucide-react';
import { useEndpoint, useUpdateEndpoint } from '@/hooks/useEndpoints';
import { ENDPOINT_CLASSES } from '@/utils/endpointLabel';
import { EndpointLinkSummary, EndpointLinkTraffic } from '@/components/EndpointLinks';
import { ManageEndpointButton } from '@/components/ManageEndpointButton';
import { ClientTrafficSection } from '@/components/traffic/ClientTrafficSection';
import type { TrafficRange } from '@/components/traffic/range';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/ui/page-container';
import { RelativeTime } from '@/components/RelativeTime';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function fmt(iso: string): string {
  return new Date(iso).toLocaleString();
}

const CLASSIFICATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'auto', label: '(let the classifier decide)' },
  ...ENDPOINT_CLASSES,
];

export default function EndpointDetails() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useEndpoint(id);
  const update = useUpdateEndpoint(id ?? '');

  const [hostname, setHostname] = useState('');
  const [classification, setClassification] = useState('auto');
  const [notes, setNotes] = useState('');
  // One range for every link's traffic, the device Traffic tab's default.
  const [trafficRange, setTrafficRange] = useState<TrafficRange>('24h');

  // Seed the form from the server copy on load and after each save (render-time, not an effect).
  const [synced, setSynced] = useState<typeof data>(undefined);
  if (data !== synced) {
    setSynced(data);
    if (data) {
      setHostname(data.hostname ?? '');
      setClassification(data.classification ?? 'auto');
      setNotes(data.notes ?? '');
    }
  }

  const handleSave = () => {
    update.mutate({
      hostname: hostname.trim() || null,
      classification: classification === 'auto' ? null : (classification || null),
      notes: notes.trim() || null,
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-9 w-80 mb-3" />
        <Skeleton className="h-4 w-48 mb-6" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load endpoint.</AlertDescription>
        </Alert>
        <Button variant="outline" asChild>
          <Link to="/network?tab=computers">
            <ChevronLeft className="w-4 h-4" />
            My Network
          </Link>
        </Button>
      </PageContainer>
    );
  }

  // IPv6 addresses contain a colon; IPv4 are dotted-quad. Numeric-aware sort
  // so 192.168.0.9 precedes 192.168.0.10.
  const ipv4 = data.ipAddresses
    .filter((ip) => !ip.includes(':'))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const ipv6 = data.ipAddresses
    .filter((ip) => ip.includes(':'))
    .sort((a, b) => a.localeCompare(b));

  const links = data.links ?? [];
  const charted = links.filter((link) => !link.behindDeviceName);
  // A client's counters are stored by its MAC, so its history outlives the association; a port's
  // traffic after the endpoint leaves is someone else's, so a cable needs a current link.
  const wirelessHistoryOnly =
    !charted.some((link) => link.kind === 'wireless') && data.classification === 'wireless_client';

  return (
    <PageContainer>
      {/* Endpoints live under the endpoint tabs, not the default (equipment)
          one — so name the tab rather than relying on /network's default. */}
      <Link
        to="/network?tab=computers"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-3 -ml-1 hover:text-foreground"
      >
        <ChevronLeft className="w-4 h-4" />
        My Network
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight font-mono break-all">
            {data.macAddress}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {data.ouiVendor ?? 'Unknown vendor'} · first seen {fmt(data.firstSeenAt)} · last seen{' '}
            {fmt(data.lastSeenAt)}
          </p>
        </div>
        <ManageEndpointButton endpoint={data} size="default" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <section className="bg-card border border-border rounded-lg p-5">
          <h2 className="text-sm font-semibold mb-4">Metadata</h2>

          <div className="mb-5 pb-5 border-b border-border flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">{links.length > 0 && !data.managedDevice ? 'Connected to' : 'Location'}</span>
              </div>
              {data.managedDevice ? (
                <>
                  <p className="text-base font-medium mt-1 break-words">
                    <Link to={`/devices/${data.managedDevice.id}`} className="hover:underline">
                      {data.managedDevice.name ?? data.managedDevice.id}
                    </Link>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This endpoint is a managed network device
                    {data.computedLocation?.interfaceName ? (
                      <>
                        {' · uplink '}
                        <code className="font-mono text-xs">
                          {data.computedLocation.deviceName ?? data.computedLocation.deviceId}:
                          {data.computedLocation.interfaceName}
                        </code>
                      </>
                    ) : null}
                  </p>
                </>
              ) : links.length > 0 ? (
                <ul className="mt-1.5 flex flex-col gap-1.5 text-sm">
                  {links.map((link) => (
                    <li key={link.linkId}>
                      <EndpointLinkSummary link={link} linkDevice />
                    </li>
                  ))}
                </ul>
              ) : data.lastKnownLocation ? (
                <>
                  <p className="text-base font-medium mt-1 break-words">
                    <Link to={`/devices/${data.lastKnownLocation.deviceId}`} className="hover:underline">
                      {data.lastKnownLocation.deviceName ?? data.lastKnownLocation.deviceId}
                    </Link>
                    {data.lastKnownLocation.interfaceName ? (
                      <>
                        {' · '}
                        <code className="font-mono text-sm">{data.lastKnownLocation.interfaceName}</code>
                      </>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Not connected right now ·{' '}
                    {data.lastKnownLocation.vlanId !== null ? <>VLAN {data.lastKnownLocation.vlanId} · </> : null}
                    last seen here <RelativeTime value={data.lastKnownLocation.lastSeenAt} className="underline decoration-dotted" />
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  Not seen on any switch port right now.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ed-hostname">Hostname</Label>
              <Input
                id="ed-hostname"
                value={hostname}
                onChange={(e) => { setHostname(e.target.value); }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ed-class">
                Classification
                {data.classificationSource ? (
                  <span className="text-muted-foreground text-xs ml-2 font-normal">
                    ({data.classificationSource === 'manual'
                      ? 'set by operator'
                      : 'auto-detected'})
                  </span>
                ) : null}
              </Label>
              <Select value={classification} onValueChange={setClassification}>
                <SelectTrigger id="ed-class">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Clearing the classification lets the periodic classifier re-evaluate this
                endpoint.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ed-notes">Notes</Label>
              <Textarea
                id="ed-notes"
                value={notes}
                onChange={(e) => { setNotes(e.target.value); }}
                rows={4}
              />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Button onClick={handleSave} disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
              {update.isError ? (
                <span className="text-xs text-destructive inline-flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Save failed.
                </span>
              ) : null}
              {update.isSuccess ? (
                <span className="text-xs text-sev-4 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Saved.
                </span>
              ) : null}
            </div>
          </div>
        </section>

        <section className="bg-card border border-border rounded-lg p-5">
          <h2 className="text-sm font-semibold mb-3">Known IP addresses</h2>
          {data.ipAddresses.length > 0 ? (
            <div className="space-y-4">
              {ipv4.length > 0 ? (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
                    IPv4
                  </h3>
                  <ul className="divide-y divide-border rounded-md border border-border">
                    {ipv4.map((ip) => (
                      <li key={ip} className="px-3 py-1.5 font-mono text-sm">
                        {ip}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {ipv6.length > 0 ? (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
                    IPv6
                  </h3>
                  <ul className="divide-y divide-border rounded-md border border-border">
                    {ipv6.map((ip) => (
                      <li key={ip} className="px-3 py-1.5 font-mono text-sm break-all">
                        {ip}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No known IP addresses.</p>
          )}
        </section>
      </div>

      {charted.length > 0 || wirelessHistoryOnly ? (
        <div>
          {charted.map((link) => (
            <EndpointLinkTraffic
              key={link.linkId}
              endpointId={data.id}
              link={link}
              range={trafficRange}
              onRangeChange={setTrafficRange}
            />
          ))}
          {wirelessHistoryOnly ? (
            <div className="mt-4 bg-card rounded-md">
              <ClientTrafficSection endpointId={data.id} range={trafficRange} onRangeChange={setTrafficRange} />
            </div>
          ) : null}
        </div>
      ) : null}
    </PageContainer>
  );
}
